import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute, Router } from '@angular/router';
import { PessoaService } from '../../../../../../services/pessoas/pessoas.service';
import { NotaService } from '../../../../../../services/financeiro/notas.service';
import { NotaDTO } from '../../../../../../models/nota.model';
import { MovimentacaoService } from '../../../../../../services/financeiro/movimentacao.service';
import { MovimentoConsultaDTO } from '../../../../../../models/movimentacao.dto';

@Component({
  selector: 'app-movimentacao-table',
  templateUrl: './movimentacao-table.component.html',
  styleUrls: ['./movimentacao-table.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class MovimentacaoTableComponent implements OnInit, OnDestroy {
  @Input() clienteID: number | undefined;
  movimentacao: MovimentoConsultaDTO[] = [] as MovimentoConsultaDTO[];
  first = 0;
  rows = 5;
  totalRecords = 0;
  lastfield = '';
  lastSort = '';
  searchCodigo: string = '';
  options = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];
  tipoDeTransacao = [
    { label: 'Todas', value: 'DESPESA,RECEITA' },
    { label: 'Receita', value: 'RECEITA' },
    { label: 'Despesa', value: 'DESPESA' },
  ];
  transacaoSelecionada = { label: 'todas', value: 'DESPESA,RECEITA' };
  rangeDates: any[] = [];
  private tipoTransacaoEnum: { [key: string]: string } = {};
  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private movimentacaoService: MovimentacaoService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.carregarTraduzoesTipoTransacao();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.carregarTraduzoesTipoTransacao();
    });

    if (!this.clienteID) {
      return;
    }
    await this.findMovimentacao(0, this.rows, 'id,asc');
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private carregarTraduzoesTipoTransacao() {
    const chaves = ['RECEITA', 'DESPESA', 'TRANSFERENCIA', 'CAMBIO', 'VENDA', 'DEVOLUCAO', 'COMPRA', 'DEVOLUCAO_DE_COMPRA'];
    this.tipoTransacaoEnum = {};
    chaves.forEach(chave => {
      this.tipoTransacaoEnum[chave] = this.translate.instant(`shared.movimentacao-table.tipo_transacao_enum.${chave}`);
    });
  }

  traduzirTipoTransacao(tipo: any): string {
    // O backend pode retornar o enum como objeto { enum: string } ou como string direta
    const tipoStr: string = (tipo && typeof tipo === 'object' && 'enum' in tipo)
      ? String(tipo.enum).toUpperCase()
      : String(tipo ?? '').toUpperCase();
    return this.tipoTransacaoEnum[tipoStr] || tipoStr;
  }
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  search() {
    this.findMovimentacao(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  findMovimentacao(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ): Promise<void> {
    return new Promise((resolve) => {
      const codigoMovimento = this.searchCodigo
        ? `&codigoMovimento=${this.searchCodigo}`
        : '';
      const transacaoSelecionada = this.transacaoSelecionada.value
        ? `&tipoDeTransacao=${this.transacaoSelecionada.value}`
        : '';
      const dataInicio =
        this.rangeDates.length > 0
          ? `&dataInicio=${this.formatDate(this.rangeDates[0])}`
          : '&dataInicio=2024-01-01';
      const dataFim =
        this.rangeDates.length > 0
          ? `&dataFim=${this.formatDate(this.rangeDates[1])}`
          : '&dataFim=2099-01-01';

      const filter =
        `pessoaId=${this.clienteID}` +
        `${codigoMovimento}` +
        `${transacaoSelecionada}` +
        `${dataInicio}` +
        `${dataFim}`;
      this.movimentacaoService
        .get('/movimento/buscar', { page, size, sort, filter })
        .subscribe(
          (response) => {
            this.movimentacao = response.content;
            this.totalRecords = response.totalElements; // Atualiza o total de registros
            this.first = page * size;
            this.rangeDates = [];
            resolve();
          },
          (error) => {
            console.error('Erro ao buscar clientes:', error);
            this.movimentacao = [];
            this.totalRecords = 0;
            this.rangeDates = [];
          }
        );
    });
  }

  onPageChange(event: any) {
    const page = event.page; // Página atual a partir do paginator
    const size = event.rows; // Quantidade de itens por página
    this.findMovimentacao(page, size);
  }

  onRowsPerPageChange() {
    this.first = 0; // Reiniciar a página para o início
    this.findMovimentacao(0, this.rows); // Buscar a primeira página com o novo tamanho
  }

  customSort(event: SortEvent) {
    let sort = 'desc';
    if (event.order === 1) {
      sort = 'asc';
    } // 1 é ascendente, -1 é descendente}

    if (this.lastfield === event.field && this.lastSort === sort) {
      return;
    } // Essencial para não cair em loop infinito

    this.lastfield = String(event.field);
    this.lastSort = String(sort);

    this.findMovimentacao(0, this.rows, `${event.field},${sort}`);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0'); // Preenche com zero à esquerda
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Meses começam em 0
    const year = String(date.getFullYear()).slice(-4); // Pega os últimos 2 dígitos do ano
    return `${year}-${month}-${day}`;
  }
}
